import { PurchaseRecord, UserSettings } from '../types';

const AI_TIER_RANK: Record<string, number> = {
    basic: 1,
    premium: 2,
    advanced: 3,
};

const getHighestAiTier = (user: UserSettings | null | undefined): 'basic' | 'premium' | 'advanced' | null => {
    if (!user?.purchaseHistory?.length) return null;

    let best: 'basic' | 'premium' | 'advanced' | null = null;
    let rank = 0;

    for (const record of user.purchaseHistory) {
        const tier = record.aiTier;
        const nextRank = tier ? (AI_TIER_RANK[tier] ?? 0) : 0;
        if (nextRank > rank) {
            best = tier;
            rank = nextRank;
        }
    }

    return best;
};

/** Gói one-time luôn active; subscription còn hạn theo packageExpiryDate */
export const isPurchaseRecordActive = (
    record: PurchaseRecord,
    user: UserSettings,
    now: Date = new Date(),
): boolean => {
    if (!record.isSubscription) return true;
    const expiry = user.packageExpiryDate ? new Date(user.packageExpiryDate) : null;
    return !expiry || expiry > now;
};

/** Giao dịch mua gần nhất trong purchaseHistory mà vẫn còn ACTIVE */
export const getMostRecentActivePurchase = (
    user: UserSettings | null | undefined,
): PurchaseRecord | null => {
    const history = user?.purchaseHistory;
    if (!history?.length || !user) return null;

    return [...history]
        .sort((a, b) => new Date(b.purchaseDate).getTime() - new Date(a.purchaseDate).getTime())
        .find(record => isPurchaseRecordActive(record, user)) ?? null;
};

export const checkCanRemoveWatermark = (user: UserSettings | null | undefined): boolean => {
    if (!user) return false;
    if (user.canRemoveWatermark === true) return true;
    if ((user as any).canRemoveWatermarkManual === true || user.isPremiumManual) return true;

    const now = new Date();
    return !!(user.purchaseHistory && user.purchaseHistory.some(record => {
        if (!record.canRemoveWatermark) return false;
        if (!record.isSubscription) return true;

        const expiryDate = user.packageExpiryDate ? new Date(user.packageExpiryDate) : null;
        return !expiryDate || expiryDate > now;
    }));
};

/**
 * Kiểm tra xem người dùng có phải là Premium hay không.
 * Premium được định nghĩa là người dùng đã từng mua gói dịch vụ
 * hoặc là thành viên của một nhóm (tổ chức/trường học).
 */
export const checkIsPremium = (user: UserSettings | null | undefined): boolean => {
    if (!user) return false;
    
    // 0. Kiểm tra flag Premium thủ công từ Admin
    if (user.isPremiumManual) return true;

    const hasPermanentHighValuePurchase = !!(user.purchaseHistory && user.purchaseHistory.some(p => {
        if (p.aiTier && AI_TIER_RANK[p.aiTier] >= AI_TIER_RANK.premium && p.isSubscription === false) {
            return true;
        }
        return p.isPremiumEligible && p.isSubscription === false;
    }));

    // Kiểm tra hết hạn nếu có
    const now = new Date();
    const expiryDate = user.packageExpiryDate ? new Date(user.packageExpiryDate) : null;
    const isExpired = expiryDate && expiryDate < now;

    // Nếu đã hết hạn thì không còn là Premium
    if (isExpired && !hasPermanentHighValuePurchase) return false;

    // 1. Kiểm tra lịch sử mua hàng
    const premiumThreshold = user.premiumCreditsThreshold ?? 1000;
    const hasPurchased = !!(user.purchaseHistory && user.purchaseHistory.some(p => {
        if (p.aiTier && AI_TIER_RANK[p.aiTier] >= AI_TIER_RANK.premium) {
            return true;
        }
        // Nếu là gói subscription, phải là gói premium eligible (năm/tổ chức)
        if (p.isSubscription) {
            return p.isPremiumEligible;
        }
        // Nếu là gói nạp lẻ (booster), phải đạt ngưỡng credit
        return p.creditsAdded >= premiumThreshold;
    }));
    
    return hasPurchased;
};

/**
 * Người dùng được phép xem bộ chọn model AI (gói gắn nhãn Premium theo cửa hàng Credit: purchase.isPremiumEligible),
 * hoặc được admin gắn isPremiumManual. Tuân kiểm tra gói / hết hạn tương tự quyền Premium (subscription dựa vào packageExpiryDate).
 */
export const userHasPremiumEligibleLabeledAccess = (
    user: UserSettings | null | undefined,
): boolean => {
    if (!user) return false;
    if (user.isPremiumManual) return true;

    const history = user.purchaseHistory || [];
    const now = new Date();
    const expiryDate = user.packageExpiryDate ? new Date(user.packageExpiryDate) : null;
    const isExpired = !!(expiryDate && expiryDate < now);

    const hasPremiumLabeledOneTime = history.some(
        (p) => p.isPremiumEligible === true && p.isSubscription === false,
    );
    if (isExpired && !hasPremiumLabeledOneTime) return false;

    return history.some((p) => {
        if (p.isPremiumEligible !== true) return false;
        if (!p.isSubscription) return true;
        return !isExpired;
    });
};

/**
 * Kiểm tra xem người dùng có phải là cấp độ "Cao cấp" (Pro hoặc Advanced) hay không.
 * Chỉ những người dùng này mới có quyền tắt quảng cáo.
 */
export const checkIsHighTierPremium = (user: UserSettings | null | undefined): boolean => {
    // Phải là Premium và chưa hết hạn trước đã
    if (!checkIsPremium(user)) return false;

    const tier = getHighestAiTier(user);
    return !!tier && AI_TIER_RANK[tier] >= AI_TIER_RANK.premium;
};
